import { useEffect, useState, useCallback, useMemo, useId, ChangeEvent } from "react";
import {
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Switch,
    SvgIcon,
    Tab,
    Tabs,
    Tooltip
} from "@mui/material";

import "./style.scss";
import { ReactComponent as CloseIcon } from "../../../assets/cross.icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/search.icon.svg";
import { ReactComponent as PlusIcon } from "../../../assets/plus.icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import {
    fetchTenants,
    createTenant,
    deleteTenant,
    fetchAdminInviteLink,
    fetchPermissionsCatalog,
    updateTenantLicensing,
    updateTenantPermissions,
    subscribeTenantUpdates
} from "../../../api/admin/tenant/endpoints-referrers";
import { AdminPermissionMeta, Tenant } from "../../../common/interfaces/admin/tenant.interface";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { ActionMenu, SelectionFilter } from "../../../components/menu-filter/menu-filter.comp";
import TenantsTable from "../../../components/admin/tenants/tenants.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import { ToastContainer } from "../../../components/toast/toast.comp";

const TenantManagement = () => {
    const { showToast } = ToastContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [checkedTenants, setCheckedTenants] = useState<Tenant[]>([]);
    const [searchString, setSearchString] = useState<string>("");
    const [searchParam, setSearchParam] = useState<string>("");
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
    const [deletableIds, setDeletableIds] = useState<number[]>([]);
    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [createForm, setCreateForm] = useState({ name: "", displayName: "", subdomain: "" });
    const [createError, setCreateError] = useState<string>("");
    // Whether an invalid submit has been attempted — gates the announced
    // (role=alert) validation summary so it doesn't fire before they try (3.3.1).
    const [showCreateErrors, setShowCreateErrors] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);

    // Tabbed properties dialog: Features (admin permissions) + Licensing
    // (question packets) — tenant_admin_permissions_design.md §4.
    const [propertiesTenant, setPropertiesTenant] = useState<Tenant | null>(null);
    const [propertiesTab, setPropertiesTab] = useState<"features" | "licensing">("features");
    const [propertiesForm, setPropertiesForm] = useState({
        enabled: false,
        startDate: "",
        limit: ""
    });
    const [initialLicensing, setInitialLicensing] = useState<string>("");
    // The permission registry (keys/labels/groups/defaults) comes from the
    // backend catalog endpoint — the frontend holds no copy (D2).
    const [permissionsCatalog, setPermissionsCatalog] = useState<AdminPermissionMeta[]>([]);
    const [permissionsForm, setPermissionsForm] = useState<Record<string, boolean>>({});
    const [initialPermissions, setInitialPermissions] = useState<string>("");
    const [isSavingProperties, setIsSavingProperties] = useState<boolean>(false);
    // Base id for associating <label htmlFor> with each field's input (WCAG 1.3.1/3.3.2).
    const fieldId = useId();

    // Sort options
    const sortOptions = ["Name", "Display Name", "Subdomain", "Tenant ID"];
    const [selectedSort, setSelectedSort] = useState<string>("Name");

    const sortedTenants = useMemo(() => {
        const fieldMap: Record<string, keyof Tenant> = {
            Name: "name",
            "Display Name": "displayName",
            Subdomain: "subdomain",
            "Tenant ID": "tenantId"
        };
        const field = fieldMap[selectedSort] || "name";
        return [...tenants].sort((a, b) => {
            const valA = (a[field] ?? "").toString().toLowerCase();
            const valB = (b[field] ?? "").toString().toLowerCase();
            return valA.localeCompare(valB);
        });
    }, [tenants, selectedSort]);

    // Create form validation
    const subdomainError = useMemo(() => {
        const val = createForm.subdomain;
        if (!val) return "";
        if (!/^[a-z0-9-]*$/.test(val))
            return "Only lowercase letters, numbers, and hyphens are allowed";
        if (val.startsWith("-") || val.endsWith("-"))
            return "Subdomain cannot start or end with a hyphen";
        if (val.length < 6) return "Subdomain must be at least 6 characters";
        return "";
    }, [createForm.subdomain]);

    const createFormValidation = useMemo(() => {
        const issues: string[] = [];
        if (!createForm.name.trim()) issues.push("Name is required");
        if (!createForm.displayName.trim()) issues.push("Display Name is required");
        if (!createForm.subdomain) {
            issues.push("Subdomain is required");
        } else if (subdomainError) {
            issues.push(subdomainError);
        }
        return { isValid: issues.length === 0, issues };
    }, [createForm, subdomainError]);

    // Load tenants
    const loadTenants = useCallback((search?: string) => {
        setIsLoading(true);
        fetchTenants(search)
            .then(response => {
                setTenants(response.data);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        loadTenants(searchParam || undefined);
    }, [loadTenants, searchParam]);

    // Debounced search
    useEffect(() => {
        const debouncer = setTimeout(() => {
            setSearchParam(searchString);
        }, 1000);
        return () => clearTimeout(debouncer);
    }, [searchString]);

    // SSE subscription
    useEffect(() => {
        const eventSource = subscribeTenantUpdates();

        eventSource.onmessage = ({ data }: { data: string }) => {
            try {
                const message = JSON.parse(data);

                if (message.type === "company_status_change") {
                    setTenants(prev =>
                        prev.map(t =>
                            t.id === message.companyId
                                ? {
                                      ...t,
                                      status: message.companyStatus,
                                      name: message.companyName ?? t.name,
                                      displayName: message.companyDisplayName ?? t.displayName,
                                      subdomain: message.companySubdomain ?? t.subdomain,
                                      tenantId: message.companyTenantId ?? t.tenantId
                                  }
                                : t
                        )
                    );
                } else if (message.type === "company_deleted") {
                    setTenants(prev => prev.filter(t => t.id !== message.companyId));
                    setCheckedTenants(prev => prev.filter(t => t.id !== message.companyId));
                }
            } catch {
                // ignore non-JSON messages (e.g. heartbeats)
            }
        };

        return () => eventSource.close();
    }, []);

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setSearchString(evt.target?.value ?? "");
    };

    const clearSearchString = () => {
        setSearchString("");
    };

    // Delete handlers
    const handleDeleteTenant = (companyId: number) => {
        setDeletableIds([companyId]);
        setDeleteConfirmationOpen(true);
    };

    const handleBulkDelete = () => {
        // Error rows may be retried (backend accepts delete from error status).
        const ids = checkedTenants
            .filter(t => t.status === "active" || t.status.startsWith("error"))
            .map(t => t.id);
        if (ids.length > 0) {
            setDeletableIds(ids);
            setDeleteConfirmationOpen(true);
        }
    };

    const handleDeleteConfirmationAction = () => {
        for (const id of deletableIds) {
            deleteTenant(id).catch(err => {
                showToast(`Failed to delete tenant: ${err.message}`, 4000);
            });
        }
        setDeleteConfirmationOpen(false);
        setCheckedTenants([]);
    };

    // Copy handlers
    const handleCopyLink = useCallback(
        (tenant: Tenant) => {
            const hostname = window.location.hostname;
            const parts = hostname.split(".");
            // Replace the first part (current subdomain) with the tenant's subdomain
            parts[0] = tenant.subdomain;
            const tenantUrl = `https://${parts.join(".")}`;
            navigator.clipboard.writeText(tenantUrl);
            showToast("Tenant link copied to clipboard", 3000);
        },
        [showToast]
    );

    const handleCopyAdminInvite = useCallback(
        (tenant: Tenant) => {
            fetchAdminInviteLink(tenant.id)
                .then(response => {
                    const code = response.data;
                    if (code) {
                        const hostname = window.location.hostname;
                        const parts = hostname.split(".");
                        parts[0] = tenant.subdomain;
                        const inviteUrl = `https://${parts.join(".")}/registration?invite=${code}`;
                        navigator.clipboard.writeText(inviteUrl);
                        showToast("Admin invite link copied to clipboard", 3000);
                    } else {
                        showToast("No admin invite found for this tenant", 3000);
                    }
                })
                .catch(() => {
                    showToast("Failed to fetch admin invite link", 3000);
                });
        },
        [showToast]
    );

    // The catalog is static registry data — fetch once for the view.
    useEffect(() => {
        fetchPermissionsCatalog()
            .then(response => setPermissionsCatalog(response.data?.items ?? []))
            .catch(() => showToast("Failed to load the permission catalog", 3000));
    }, [showToast]);

    // Resolve a tenant's sparse overrides against the catalog defaults.
    const resolvePermissions = useCallback(
        (overrides: Record<string, boolean> | null | undefined): Record<string, boolean> =>
            Object.fromEntries(
                permissionsCatalog.map(p => [p.key, overrides?.[p.key] ?? p.default])
            ),
        [permissionsCatalog]
    );

    const permissionGroups = useMemo(() => {
        const groups: string[] = [];
        for (const p of permissionsCatalog) {
            if (!groups.includes(p.group)) groups.push(p.group);
        }
        return groups;
    }, [permissionsCatalog]);

    // Properties dialog handlers
    const handleProperties = useCallback(
        (tenant: Tenant) => {
            setPropertiesTenant(tenant);
            setPropertiesTab("features");
            const licensing = {
                enabled: !!tenant.questionLicensingEnabled,
                startDate: tenant.questionCountStartDate
                    ? tenant.questionCountStartDate.slice(0, 10)
                    : "",
                limit:
                    tenant.questionLimit !== null && tenant.questionLimit !== undefined
                        ? String(tenant.questionLimit)
                        : ""
            };
            setPropertiesForm(licensing);
            setInitialLicensing(JSON.stringify(licensing));
            const resolved = resolvePermissions(tenant.adminPermissions);
            setPermissionsForm(resolved);
            setInitialPermissions(JSON.stringify(resolved));
        },
        [resolvePermissions]
    );

    const setAllPermissions = (value: boolean) =>
        setPermissionsForm(Object.fromEntries(permissionsCatalog.map(p => [p.key, value])));

    const resetPermissionsToDefaults = () =>
        setPermissionsForm(Object.fromEntries(permissionsCatalog.map(p => [p.key, p.default])));

    const handleSaveProperties = async () => {
        if (!propertiesTenant) return;
        setIsSavingProperties(true);
        const licensingChanged = JSON.stringify(propertiesForm) !== initialLicensing;
        const permissionsChanged = JSON.stringify(permissionsForm) !== initialPermissions;
        const payload = {
            enabled: propertiesForm.enabled,
            startDate: propertiesForm.startDate || null,
            limit: propertiesForm.limit === "" ? null : Number(propertiesForm.limit)
        };
        try {
            // Licensing only when touched — its PATCH resets the usage cache
            // and re-arms the 90% warning as a side effect.
            if (licensingChanged) {
                await updateTenantLicensing(propertiesTenant.id, payload);
            }
            // The server stores only values that differ from the defaults.
            let savedOverrides = propertiesTenant.adminPermissions ?? null;
            if (permissionsChanged) {
                const response = await updateTenantPermissions(
                    propertiesTenant.id,
                    permissionsForm
                );
                savedOverrides = response.data?.adminPermissions ?? null;
            }
            setTenants(prev =>
                prev.map(t =>
                    t.id === propertiesTenant.id
                        ? {
                              ...t,
                              ...(licensingChanged
                                  ? {
                                        questionLicensingEnabled: payload.enabled,
                                        questionCountStartDate: payload.startDate,
                                        questionLimit: payload.limit
                                    }
                                  : {}),
                              adminPermissions: savedOverrides
                          }
                        : t
                )
            );
            showToast("Tenant properties saved", 3000);
            setPropertiesTenant(null);
        } catch (err: any) {
            const message =
                err.response?.data?.message || err.message || "Failed to save properties";
            showToast(message, 3000);
        } finally {
            setIsSavingProperties(false);
        }
    };

    // Create handlers
    const handleCreateTenant = () => {
        setCreateForm({ name: "", displayName: "", subdomain: "" });
        setCreateError("");
        setShowCreateErrors(false);
        setCreateModalOpen(true);
    };

    const handleCreateFormChange = (field: string, value: string) => {
        setCreateForm(prev => ({ ...prev, [field]: value }));
        setCreateError("");
    };

    const handleCreateSubmit = () => {
        if (!createFormValidation.isValid) {
            setShowCreateErrors(true);
            return;
        }
        setIsCreating(true);
        createTenant(createForm)
            .then(response => {
                setTenants(prev => [...prev, response.data]);
                setCreateModalOpen(false);
                showToast("Tenant created successfully", 3000);
                setIsCreating(false);
            })
            .catch(err => {
                const message =
                    err.response?.data?.message || err.message || "Failed to create tenant";
                setCreateError(message);
                setIsCreating(false);
            });
    };

    return (
        <HeaderLayout blurHeader={false}>
            <div className="tenant-management">
                <div className="tenant-management-title">
                    <span className="tenant-management-title__text" role="heading" aria-level={1}>
                        Tenants
                    </span>
                    <div className="tenant-management-title-search">
                        <SvgIcon
                            className="tenant-management-title-search__icon"
                            component={SearchIcon}
                            inheritViewBox
                        />
                        <input
                            type="text"
                            aria-label="Search tenants"
                            placeholder="Search"
                            onChange={handleSearchChange}
                            value={searchString}
                        />
                        {!!searchString && (
                            <IconButton
                                className="tenant-management-title-search__button--clear"
                                aria-label="Clear search"
                                onClick={clearSearchString}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </div>
                </div>
                <div className="tenant-management-filters">
                    <div className="tenant-management-filter tenant-management-filter--actions">
                        <ActionMenu
                            label="Action"
                            disabled={checkedTenants.length === 0}
                            actions={[{ name: "Delete", action: handleBulkDelete }]}
                            minWidth={160}
                        />
                    </div>
                    <div className="tenant-management-filter tenant-management-filter--sorting">
                        <SelectionFilter
                            list={sortOptions.map(o => `Sort by: ${o}`)}
                            selectedItem={`Sort by: ${selectedSort}`}
                            setSelectedItem={(val: string) =>
                                setSelectedSort(val.replace("Sort by: ", ""))
                            }
                            rightPosition={true}
                            minWidth={200}
                        />
                    </div>
                    <Button
                        className="tenant-management-create-button"
                        variant="text"
                        startIcon={
                            <SvgIcon
                                className="tenant-management-create-button__icon"
                                component={PlusIcon}
                                inheritViewBox
                            />
                        }
                        onClick={handleCreateTenant}
                    >
                        Create Tenant
                    </Button>
                </div>
                <div className="tenant-management-table">
                    <TenantsTable
                        items={sortedTenants}
                        checkedItems={checkedTenants}
                        setCheckedItems={setCheckedTenants}
                        handleDelete={handleDeleteTenant}
                        handleCopyLink={handleCopyLink}
                        handleCopyAdminInvite={handleCopyAdminInvite}
                        handleProperties={handleProperties}
                    />
                    <LoaderView show={isLoading} />
                </div>
            </div>

            {/* Create Tenant Modal */}
            <ModalTemplate
                open={createModalOpen}
                width={500}
                handleClose={() => setCreateModalOpen(false)}
                ariaLabel="Create Tenant"
            >
                <div className="create-tenant-form">
                    <h2 className="create-tenant-form__title">Create Tenant</h2>
                    <div className="create-tenant-form__field">
                        <label htmlFor={`${fieldId}-name`}>Name</label>
                        <input
                            id={`${fieldId}-name`}
                            type="text"
                            value={createForm.name}
                            onChange={e => handleCreateFormChange("name", e.target.value)}
                            placeholder="Tenant name"
                        />
                    </div>
                    <div className="create-tenant-form__field">
                        <label htmlFor={`${fieldId}-displayName`}>Display Name</label>
                        <input
                            id={`${fieldId}-displayName`}
                            type="text"
                            value={createForm.displayName}
                            onChange={e => handleCreateFormChange("displayName", e.target.value)}
                            placeholder="Display name"
                        />
                    </div>
                    <div className="create-tenant-form__field">
                        <label htmlFor={`${fieldId}-subdomain`}>Subdomain</label>
                        <input
                            id={`${fieldId}-subdomain`}
                            type="text"
                            value={createForm.subdomain}
                            onChange={e =>
                                handleCreateFormChange("subdomain", e.target.value.toLowerCase())
                            }
                            placeholder="subdomain (min 6 characters)"
                            aria-invalid={subdomainError ? true : undefined}
                            aria-describedby={
                                subdomainError ? `${fieldId}-subdomain-error` : undefined
                            }
                        />
                        {subdomainError && (
                            <span
                                id={`${fieldId}-subdomain-error`}
                                className="create-tenant-form__field-error"
                            >
                                {subdomainError}
                            </span>
                        )}
                    </div>
                    {showCreateErrors && !createFormValidation.isValid && (
                        <div className="create-tenant-form__error" role="alert">
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {createFormValidation.issues.map((issue, i) => (
                                    <li key={i}>{issue}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {createError && (
                        <div className="create-tenant-form__error" role="alert">
                            {createError}
                        </div>
                    )}
                    <div className="create-tenant-form__actions">
                        <Button
                            className="create-tenant-form__cancel"
                            variant="text"
                            onClick={() => setCreateModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Tooltip
                            title={
                                !createFormValidation.isValid ? (
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        {createFormValidation.issues.map((issue, i) => (
                                            <li key={i}>{issue}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    ""
                                )
                            }
                            disableHoverListener={createFormValidation.isValid}
                        >
                            <span>
                                <Button
                                    className="create-tenant-form__submit"
                                    variant="contained"
                                    onClick={handleCreateSubmit}
                                    disabled={isCreating}
                                >
                                    {isCreating ? "Creating..." : "Create"}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </ModalTemplate>

            {/* Tenant Properties Modal: Features (admin permissions) + Licensing */}
            <ModalTemplate
                open={!!propertiesTenant}
                width={720}
                handleClose={() => setPropertiesTenant(null)}
                ariaLabel="Tenant properties"
            >
                <div className="create-tenant-form tenant-properties">
                    <h2 className="create-tenant-form__title">
                        Properties{propertiesTenant ? ` — ${propertiesTenant.displayName}` : ""}
                    </h2>
                    <Tabs
                        value={propertiesTab}
                        onChange={(_, v) => setPropertiesTab(v)}
                        className="tenant-properties__tabs"
                    >
                        <Tab value="features" label="Features" />
                        <Tab value="licensing" label="Licensing" />
                    </Tabs>
                    {propertiesTab === "features" && (
                        <div className="tenant-permissions">
                            <p className="tenant-permissions__hint">
                                What this tenant&apos;s admins can use. Super admins always see
                                everything; changes take effect on the admin&apos;s next sign-in or
                                page reload.
                            </p>
                            <div className="tenant-permissions__toolbar">
                                <Button variant="text" onClick={() => setAllPermissions(true)}>
                                    Select all
                                </Button>
                                <Button variant="text" onClick={() => setAllPermissions(false)}>
                                    Deselect all
                                </Button>
                                <Button variant="text" onClick={resetPermissionsToDefaults}>
                                    Reset to defaults
                                </Button>
                            </div>
                            {permissionsCatalog.length === 0 && (
                                <p className="tenant-permissions__hint">Loading catalog…</p>
                            )}
                            {permissionGroups.map(group => (
                                <div key={group} className="tenant-permissions__group">
                                    <div
                                        className="tenant-permissions__group-title"
                                        role="heading"
                                        aria-level={3}
                                    >
                                        {group}
                                    </div>
                                    <div className="tenant-permissions__grid">
                                        {permissionsCatalog
                                            .filter(p => p.group === group)
                                            .map(p => (
                                                <Tooltip
                                                    key={p.key}
                                                    title={p.description}
                                                    placement="right"
                                                    enterDelay={300}
                                                    describeChild
                                                >
                                                    <FormControlLabel
                                                        className="tenant-permissions__item"
                                                        control={
                                                            <Checkbox
                                                                checked={!!permissionsForm[p.key]}
                                                                onChange={e =>
                                                                    setPermissionsForm(prev => ({
                                                                        ...prev,
                                                                        [p.key]: e.target.checked
                                                                    }))
                                                                }
                                                            />
                                                        }
                                                        label={`${p.label} (default ${p.default ? "on" : "off"})`}
                                                    />
                                                </Tooltip>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {propertiesTab === "licensing" && (
                        <>
                            <div className="create-tenant-form__field">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={propertiesForm.enabled}
                                            onChange={e =>
                                                setPropertiesForm(prev => ({
                                                    ...prev,
                                                    enabled: e.target.checked
                                                }))
                                            }
                                        />
                                    }
                                    label={
                                        propertiesForm.enabled
                                            ? "Question counting enabled"
                                            : "Question counting disabled"
                                    }
                                />
                            </div>
                            <div className="create-tenant-form__field">
                                <label htmlFor={`${fieldId}-startDate`}>Count start date</label>
                                <input
                                    id={`${fieldId}-startDate`}
                                    type="date"
                                    value={propertiesForm.startDate}
                                    onChange={e =>
                                        setPropertiesForm(prev => ({
                                            ...prev,
                                            startDate: e.target.value
                                        }))
                                    }
                                />
                            </div>
                            <div className="create-tenant-form__field">
                                <label htmlFor={`${fieldId}-limit`}>Questions purchased</label>
                                <input
                                    id={`${fieldId}-limit`}
                                    type="number"
                                    min={0}
                                    value={propertiesForm.limit}
                                    onChange={e =>
                                        setPropertiesForm(prev => ({
                                            ...prev,
                                            limit: e.target.value
                                        }))
                                    }
                                    placeholder="e.g. 10000"
                                />
                            </div>
                            <div className="create-tenant-form__field">
                                {/* Read-only value, not a form control — caption, not a <label>. */}
                                <span className="create-tenant-form__field-caption">
                                    Questions used
                                </span>
                                <span className="tenant-cell-text">
                                    {propertiesTenant?.questionsUsed ?? 0}
                                </span>
                            </div>
                        </>
                    )}
                    <div className="create-tenant-form__actions">
                        <Button
                            className="create-tenant-form__cancel"
                            variant="text"
                            onClick={() => setPropertiesTenant(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="create-tenant-form__submit"
                            variant="contained"
                            onClick={handleSaveProperties}
                            disabled={isSavingProperties}
                        >
                            {isSavingProperties ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </div>
            </ModalTemplate>

            {/* Delete Confirmation */}
            <ConfirmationPopup
                open={deleteConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Delete"
                description={`You are about to delete ${deletableIds.length} tenant(s). This will remove all users, knowledge bases, and authentication data. This can't be undone!`}
                actionName="Delete"
                handleAction={handleDeleteConfirmationAction}
                handleClose={() => setDeleteConfirmationOpen(false)}
            />
        </HeaderLayout>
    );
};

export default TenantManagement;
